"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PlayerClasses = exports.Locales = exports.SigningKeyStatuses = exports.ZoneKinds = exports.InsertModes = exports.TriggerTypes = exports.ComposerKinds = exports.GroupKinds = exports.OperationalStatuses = exports.LifecycleStatuses = exports.DeviceTypes = exports.PairingStates = exports.AuditActions = exports.ManifestKinds = exports.Roles = exports.AssignableRoles = exports.ResourceStates = exports.ResourceKinds = void 0;
exports.ResourceKinds = ['image', 'video', 'audio', 'rss_feed', 'web_page'];
exports.ResourceStates = [
    'draft',
    'pending_review',
    'recommended',
    'approved',
    'revoked',
    'expired',
];
// 'attendant' added in Queue Engine Spec 1 Task 2.1 (lockstep with the
// Prisma Role enum ALTER TYPE; see commit
// 20260527140000_role_add_attendant). The TS union here mirrors the DB
// enum and MUST stay in lockstep — admin-users.service.ts and
// auth.controller.ts narrow `prisma.user.roles: Role[]` to this union.
// Per-tenant ASSIGNABLE roles — the set a tenant admin may grant through the
// per-tenant user CRUD (`/admin/users`). Deliberately EXCLUDES 'super_admin'
// so a tenant admin can never mint a platform operator (privilege escalation).
exports.AssignableRoles = ['admin', 'operator', 'auditor', 'attendant'];
// All roles, incl. the PLATFORM-level 'super_admin' (sub-project 3 onboarding):
// it lives in the dedicated platform tenant and manages tenant organizations.
// It is granted ONLY via the seed / tenant-provisioning path — never the
// per-tenant user CRUD — and does NOT imply per-tenant `admin` powers.
exports.Roles = [...exports.AssignableRoles, 'super_admin'];
exports.ManifestKinds = [
    'displaycms.asset.approval/v1',
    'displaycms.source.approval/v1',
    'displaycms.composer.approval/v1',
];
exports.AuditActions = [
    'submit_for_review',
    'recommend_approve',
    'approve',
    'reject',
    'revoke',
    'mark_permanent',
    'reattest',
    // P3 W2 B9s (FS §8.5): an admin force-assigned a composer to a
    // player/group bypassing the capability check. Appended (not
    // reordered) so existing ordinal-free consumers (z.enum, the admin
    // audit-filter chip map) are unaffected; the dual-approval DB trigger
    // keys only on 'approve' so this value passes it untouched.
    'composer_assigned_force',
    // P5 W1: operator-command audit trail. `command_issued` is emitted by
    // the server on POST /admin/players/:id/commands; `command_completed`
    // is emitted when the player ACKs over the playback.event WS channel.
    // Same append-only invariant as composer_assigned_force above.
    'command_issued',
    'command_completed',
    // P5 W2 §7.2: abnormal-detection sweep. Appended last; do NOT
    // reorder or remove. The dedup window and rationale context are
    // serialised as JSON in the `rationale` text column (schema has no
    // rationaleJson column). The actor_id is set to the tenant's first
    // admin user (synthetic system actor) because AuditEvent.actor_id
    // is a required FK to User in the current schema; a dedicated
    // system-user row or a nullable actor column is W3 graduation scope.
    'abnormal_detected',
    // P5 W4 §4: managed software/firmware release lifecycle. Appended
    // last; do NOT reorder or remove. Four distinct events so the audit
    // trail surfaces each phase of the bundle lifecycle independently:
    //   software_uploaded  — admin uploads a new SoftwareBundle (draft).
    //   software_approved  — approver transitions bundle → approved.
    //   software_revoked   — admin/operator revokes an approved bundle.
    //   upgrade_failed     — player ACKed the upgrade command with an error.
    'software_uploaded', // P5 W4
    'software_approved', // P5 W4
    'software_revoked', // P5 W4
    'upgrade_failed', // P5 W4
    // P5 W7 T6: queue counter admin CRUD audit trail. Appended last;
    // do NOT reorder or remove. AuditActions enum APPEND-ONLY invariant.
    'queue_counter_created', // P5 W7 T6
    'queue_counter_updated', // P5 W7 T6
    'queue_counter_deleted', // P5 W7 T6
    // P5 W7 T7: webhook secret mint + revoke audit trail. Appended last;
    // do NOT reorder or remove. AuditActions enum APPEND-ONLY invariant.
    'queue_webhook_secret_created', // P5 W7 T7
    'queue_webhook_secret_revoked', // P5 W7 T7
    // P5 W7 T8: admin test-event bypass audit trail. Appended last;
    // do NOT reorder or remove. AuditActions enum APPEND-ONLY invariant.
    'queue_test_event_emitted', // P5 W7 T8
    // Bulk CSV user import: emitted per created user in importCsv.
    // Appended last; do NOT reorder or remove. APPEND-ONLY invariant.
    'user_created', // bulk CSV import
    // Spec 1 Queue Engine — queue lifecycle. Appended last; APPEND-ONLY.
    // Distinct from legacy `queue_counter_*` (which is for the legacy
    // QueueCounter table, kept untouched). These 10 belong to the new
    // queue-engine domain (queue/, physical_counter/).
    'queue_created', // queue-engine
    'queue_updated', // queue-engine
    'queue_paused', // queue-engine
    'queue_resumed', // queue-engine
    'queue_closed', // queue-engine
    'queue_reopened', // queue-engine
    'queue_schedule_changed', // queue-engine
    'counter_created', // queue-engine (physical_counter, NOT legacy)
    'counter_updated', // queue-engine
    'counter_deleted', // queue-engine
    // Spec 5 Restaurant extension — menu item lifecycle. Appended last;
    // APPEND-ONLY invariant. Order/positions of preceding entries are
    // byte-untouched. Orders themselves do not emit audit events (volume);
    // the order_ext row + state column carry the forensic info.
    'menu_item_created', // restaurant
    'menu_item_updated', // restaurant
    'menu_item_deleted', // restaurant
    // P4 W1 T5: device control command audit trail. Appended last;
    // do NOT reorder or remove. AuditActions enum APPEND-ONLY invariant.
    // action is written to the free-string `action` column (no DB enum).
    'device.command_issued', // P4 W1 T5
    // P4 W2 T1: device-owner command kinds audit trail. Appended last;
    // do NOT reorder or remove. AuditActions enum APPEND-ONLY invariant.
    'device.reboot', // P4 W2 T1
    'device.screenshot', // P4 W2 T1
];
exports.PairingStates = ['unpaired', 'paired', 'revoked'];
// Screen-inventory (SP2): the physical screen type + admin lifecycle.
exports.DeviceTypes = ['kiosk', 'tv', 'led', 'tablet', 'projector', 'display', 'unknown'];
exports.LifecycleStatuses = ['active', 'maintenance', 'broken', 'end_of_life'];
// Derived live operational status (only meaningful when lifecycle='active').
exports.OperationalStatuses = ['running', 'idle', 'down'];
// Group location kind (building→room hierarchy).
exports.GroupKinds = ['site', 'building', 'floor', 'room', 'zone'];
exports.ComposerKinds = ['loop', 'demand', 'insert', 'queue_overlay'];
exports.TriggerTypes = ['remote', 'touch', 'inductor', 'network'];
exports.InsertModes = ['always', 'interval'];
exports.ZoneKinds = ['media', 'hybrid', 'device', 'text'];
exports.SigningKeyStatuses = ['active', 'retiring', 'revoked'];
exports.Locales = ['fr', 'en'];
exports.PlayerClasses = ['A', 'B', 'C', 'D'];
//# sourceMappingURL=enums.js.map